import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FaSearch, FaRobot, FaSatelliteDish, FaMobileAlt,
  FaShieldAlt, FaChartBar, FaPalette, FaGlobe, FaDatabase,
} from 'react-icons/fa';

const features = [
  {
    id: 'unified-discovery',
    title: 'Unified Discovery',
    icon: <FaSearch />,
    desc: 'Search and access across eBooks, Journals, LMS, Rare Books, and more in one place.',
    details: `
    Discover smarter academic solutions with iDisKover—a cutting-edge search platform harnessing semantic understanding and advanced AI models, including OpenAI technologies.
    iDisKover is revolutionizing research with intelligent recommendations tailored to your needs. The platform combines transformer-based search capabilities with contextual insights to deliver the most relevant academic content effortlessly.
    Resources configuration include:
    • J-Gate Journals Database - Access billions of peer-reviewed articles across disciplines.
    • DSpace Institutional Repository - Discover theses, dissertations, and institutional research archives.
    • Koha Library Management System - Check OPAC, availability, and circulation info of physical library books.
    • Subscribed eBooks & Open Access - Search both licensed and open educational resources seamlessly.
    This holistic search ensures users don’t have to “know where to look” — iDisKover finds relevant knowledge no matter where it lives.
  `
  },
  {
    id: 'ai-powered-search',
    title: 'AI-Powered Search',
    icon: <FaRobot />,
    desc: 'Semantic search and intelligent recommendations powered by OpenAI and custom AI models.',
    details: `
      Discover smarter academic solutions with iDisKover—a cutting-edge search platform harnessing semantic understanding and advanced AI models, including OpenAI technologies.
      iDisKover is revolutionizing research with intelligent recommendations tailored to your needs. The platform combines transformer-based search capabilities with contextual insights to deliver the most relevant academic content effortlessly.
      Features That Make Us Stand Out:
      • Seamless search across multiple academic resources, including eBooks, journals, and repositories. 
      • Keyword expansion and concept-based matching for more intuitive discovery.
      • Sophisticated AI-driven ranking that adapts to your usage patterns and leverages metadata for unparalleled relevance.
    `
  },
  {
    id: 'api-integration',
    title: 'API Integration',
    icon: <FaSatelliteDish />,
    desc: 'Connects to J-Gate, KOHA LMS, DSpace, OpenAlex, Semantic Scholar, and many more in real time.',
    details: `
    iDisKover's robust API integration framework brings together diverse academic data sources into a single seamless experience - fully in real time. Explore effortlessly with iDisKover's advanced API integration system - bringing academic resources together into one intuitive and unified experience, all in real time.
    Connected platforms include:
    • J-Gate Journals Platform - Navigate through an extensive archive of academic journal articles, complete with relevance-based sorting and filtering options.
    • KOHA Library Management System - Retrieve realtime OPAC metadata, item availability, and circulation insights instantly.
    • DSpace Institutional Repository - Search and preview documents hosted within the institution's repository.
    • Subscribed Publishers - Search across all subscribed publisher content in one consolidated discovery interface.
  `
  },
  {
    id: 'mobile-app',
    title: 'Mobile App Access',
    icon: <FaMobileAlt />,
    desc: 'iDisKover is available on Android and iOS with full offline and installable PWA support.',
    details: `
    iDisKover is crafted to empower students, educators, and professionals with seamless connectivity to academic resources, anytime and anywhere.
    Platform Compatibility includes:
    • Android Native App: A smooth, optimized experience that supports in-app authentication, ensuring secure access on the go.
    • iOS Native App: Intuitive navigation and compatibility across iPhones and iPads, offering a premium reading and browsing experience.
    • Progressive Web App (PWA): Combines the best of web and app experiences with offline support, fast performance, and the ease of installation straight from the browser.
    • Responsive Web Interface: Accessible across devices—desktops, tablets, and smartphones—without compromising on functionality or usability.
    Users can effortlessly search, read full-text materials, bookmark valuable resources, and stay informed through smart notifications—all within a sleek, user-friendly interface designed for maximum productivity.
  `
  },
  {
    id: 'secure-authentication',
    title: 'Secure Authentication',
    icon: <FaShieldAlt />,
    desc: 'SSO via SAML, OAuth, IP login, LDAP, and social logins with role-based access.',
    details: `
    iDisKover offers a robust and customizable authentication ecosystem designed to meet the demands of institutional access. It ensures a seamless balance between security, adaptability, and an effortless user experience.
    Supported authentication methods include:
    • IP-Based Authentication: Hassle-free access within institutional networks or via RemotLog proxy services for remote connectivity.
    • SAML 2.0: Single Sign-On (SSO) integration with institutional identity providers for streamlined user authentication.
    • OAuth 2.0: Secure access through trusted platforms such as Google, Microsoft, and other OAuth-compliant providers.
    • LDAP / Active Directory: Lightweight and dynamic directory access with comprehensive user role mapping.
    • Social Logins: Optional access via popular platforms like Google, Facebook, and LinkedIn to support varied user preferences.
    • Role-Based Access Control: Flexible permission and access level management based on user roles, such as student, faculty, or admin.
    Every authentication transaction is encrypted, audited, and session-managed to comply with institutional IT policies and global data protection standards. Designed for scalability to cater to institutions of all sizes, from small colleges to large research organizations.
  `
  },
  {
    id: 'admin-dashboard',
    title: 'Admin Dashboard',
    icon: <FaChartBar />,
    desc: 'Manage users, access, content and subscriptions. Includes powerful analytics.',
    details: `
    The iDisKover Admin Dashboard is your comprehensive control hub, designed to streamline the configuration, management, and monitoring of your digital library platform.
    Key capabilities include:
    • User Management - Effortlessly view, add, and manage users with role-based access, including admins, faculty, students, and librarians.
    • eResource Control - Administer subscriptions, licensed content, and authentication settings with precision.
    • Dashboard Overview - Gain a visual snapshot of daily activity, new sign-ups, popular resources, and usage trends.
    • Deep Analytics - Track detailed statistics such as content views, downloads, user sessions, device types, and geographic access patterns.
    • Usage Reports - Generate institutional and departmental reports in Excel/CSV formats for audits or presentations.
    • Alerts & Notifications - Stay informed with alerts on subscription expirations, high-traffic events, and unusual login activities.
    Built with an intuitive, user-friendly interface, the iDisKover Admin Dashboard ensures secure access exclusively for verified admin roles, offering a seamless experience in digital library management.
  `
  },
  {
    id: 'white-label',
    title: 'White-Labeled Portal',
    icon: <FaPalette />,
    desc: 'Custom branding, institutional logos, and UI tailored to your identity.',
    details: `
    iDisKover enables institutions to create a seamless, fully branded digital library that reflects their unique identity and values.
    Custom branding features include:
    • Institutional Logo & Name - Showcase your university or organization's branding prominently across the platform.
    • Custom Color Scheme - Ensure design consistency by aligning the UI with your institution’s official color palette.
    • Login Page Personalization - Customize banners, welcome messages, and institutional instructions for a tailored login experience.
    • Domain Mapping - Host iDisKover under your custom domain for an integrated experience.
    • Footer & Contact Details - Display relevant contact information, helpline support, and institutional credits in the footer.
    • Home Banner & Announcements - Highlight personalized messages and important updates directly on the landing page.
    With iDisKover, your institution's digital library remains fully functional while seamlessly blending into your existing digital ecosystem, ensuring a professional and cohesive experience for users.
  `
  },
  {
    id: 'security-compliance',
    title: 'Enterprise-Grade Security',
    icon: <FaGlobe />,
    desc: 'GDPR & ISO 27001 compliance with anomaly detection and advanced session management.',
    details: `
    Designed with uncompromising security standards, iDisKover ensures institutions can focus on their goals while safeguarding sensitive information.
    Security highlights include:
    • GDPR Compliance - Fully aligned with EU General Data Protection Regulation, including data minimization, consent handling, and user data rights.
    • ISO 27001 Standards - Developed with best practices in information security management, policies, and controls.
    • Anomaly Detection - Monitors abnormal login patterns, excessive downloads, and unusual access behaviors to prevent abuse.
    • Role-Based Access Control (RBAC) - Define and restrict platform actions by user type (admin, student, factulty, librarian, guest).
    • Advanced Authentication Protocols: Employs cutting-edge security measures such as IP whitelisting, SAML, OAuth, multi-factor authentication, and encrypted session tokens.
    • Comprehensive Audit Logs: Captures every access and administrative action with timestamps for maximum traceability.
    • Automatic Session Management: Enhances safety through auto-timeouts, session invalidation during inactivity, and secure logout procedures.
    Security features are reviewed regularly, and the platform is penetration-tested to ensure data confidentiality and platform integrity. Additionally, regular monitoring fortify iDisKover's security framework, guaranteeing data confidentiality.
  `
  },
  {
    id: 'institutional-repository',
    title: 'Institutional Repository',
    icon: <FaDatabase />,
    desc: 'Cloud-hosted DSpace (2TB) for thesis, publications, and learning resources.',
    details: `
    A fully managed, cloud-hosted DSpace repository, iDisKover empowers institutions to preserve, showcase, and disseminate academic and educational content effortlessly.
    Repository features include:
    • Trusted DSpace Foundation: Built on the globally recognized open-source DSpace platform, relied on by thousands of academic institutions.
    • Generous 2TB Cloud Storage: Securely house theses, dissertations, conference proceedings, student projects, and faculty research.
    • Advanced metadata Search: Improve discoverability with support for Dublin Core, MARC, and custom schema.
    • Organized Community & Collections: Structure content by departments, faculty, or subjects with role-based contributor rights.
    • Persistent Identifiers: Seamlessly integrate DOI and ORCID to enhance the visibility of publications and researcher profiles.
    • Robust Full-Text Indexing: Enable sophisticated keyword and semantic searches across uploaded files and metadata fields.
    • OAI-PMH Compatibility: Ensure wider reach through harvestable metadata for institutional catalogs and aggregators.
    iDisKover's repository is institution-branded, fortified with security, and integrated into the platform for unified search capabilities. Academic excellence meets effortless management.
  `
  },
];

export default function FeaturesPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const section = document.querySelector(hash);
      if (section) {
        setTimeout(() => {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [hash]);

  return (
    <section className="relative py-16 px-6 bg-gradient-to-r from-[#fdfdfd] to-[#eef5f9] overflow-hidden">
      {/* Bubbles */}
      <div className="absolute -top-16 -left-16 w-96 h-96 bg-[#ff7a00]/10 rounded-full blur-3xl z-0"></div>
      <div className="absolute top-1/3 left-1/2 w-72 h-72 bg-[#055862]/10 rounded-full blur-2xl z-0"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#ff7a00]/20 rounded-full blur-3xl z-0"></div>
  
      {/* Foreground */}
      <div className="relative z-10 container mx-auto max-w-5xl">
        <h1 className="text-4xl font-bold text-[#055862] mb-10 text-center" style={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 0.15)' }}>
          iDisKover Key Features
        </h1>
  
        <div className="space-y-12">
          {features.map((feature) => (
            <div key={feature.id} id={feature.id} className="group bg-white/90 backdrop-blur-md rounded-xl p-6 shadow-md border border-gray-200 hover:border-[#ff7a00] hover:shadow-xl hover:scale-[1.01] transition-all duration-300 scroll-mt-24">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-[#ff7a00]/10 p-3 rounded-full text-[#ff7a00] text-2xl">{feature.icon}</div>
                <h2 className="text-xl sm:text-2xl font-semibold text-[#055862] group-hover:underline">{feature.title}</h2>
              </div>
              <p className="text-gray-700 text-base sm:text-lg">{feature.desc}</p>
              <div className="mt-3 text-gray-600 bg-white p-4 rounded-lg border-l-4 border-[#ff7a00] shadow-inner text-sm sm:text-base leading-relaxed space-y-2">
                {feature.details.split('\n').map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
  
}
